/*eslint-disable*/
import React from "react";
import {
  useTable,
  useFilters,
  useSortBy,
  usePagination,
} from "react-table";
import classnames from "classnames";
import { matchSorter } from "match-sorter";
import { makeStyles } from "@material-ui/core/styles";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import CustomInput from "components/CustomInput/CustomInput.js";

import styles from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import { useTranslation } from "react-i18next";

const newStyles = {
  ...styles,

  formControlMargins: {
    margin: "3px 0 !important",
  },

  wrapper: {
    width: "100%",
  },

  controlsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "12px",

    "@media (max-width: 960px)": {
      justifyContent: "center",
    },
  },

  navButtons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    flexWrap: "wrap",
  },

  paginationSummary: {
    fontSize: "0.875rem",
    color: "#555",
    textAlign: "center",
    wordBreak: "break-word",
  },

  selectsWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    flexWrap: "wrap",
    width: "100%",
    maxWidth: "520px",
  },

  selectControl: {
    minWidth: "170px",
    flex: "1 1 170px",
    maxWidth: "240px",

    "@media (max-width: 600px)": {
      minWidth: "100%",
      maxWidth: "100%",
      flex: "1 1 100%",
    },
  },

  tableScroll: {
    width: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },

  table: {
    minWidth: "720px",

    "@media (max-width: 960px)": {
      minWidth: "640px",
    },

    "@media (max-width: 600px)": {
      minWidth: "560px",
    },
  },

  headerCell: {
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    whiteSpace: "normal",
    lineHeight: 1.4,
  },

  cellContent: {
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    whiteSpace: "normal",
    lineHeight: 1.45,
  },
};

const useStyles = makeStyles(newStyles);

function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const { t } = useTranslation("search");
  const count = preFilteredRows.length;

  return (
    <CustomInput
      formControlProps={{
        fullWidth: true,
      }}
      inputProps={{
        value: filterValue || "",
        onChange: (e) => {
          setFilter(e.target.value || undefined);
        },
        placeholder: t("reacttable-filter-placeholder", { count }),
      }}
    />
  );
}

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
}

fuzzyTextFilterFn.autoRemove = (val) => !val;

function Table({ columns, data }) {
  const { t } = useTranslation("search");
  const classes = useStyles();

  const [numberOfRows, setNumberOfRows] = React.useState(10);
  const [pageSelect, setPageSelect] = React.useState(0);

  const filterTypes = React.useMemo(
    () => ({
      fuzzyText: fuzzyTextFilterFn,
      text: (rows, id, filterValue) =>
        rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        }),
    }),
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    pageOptions,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    setPageSize,
    gotoPage,
    state: tableState,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      filterTypes,
      initialState: { pageSize: 10, pageIndex: 0 },
    },
    useFilters,
    useSortBy,
    usePagination
  );

  React.useEffect(() => {
    setPageSelect(tableState.pageIndex);
    setNumberOfRows(tableState.pageSize);
  }, [tableState.pageIndex, tableState.pageSize]);

  const numberOfRowsData = [5, 10, 20, 25, 50, 100];

  return (
    <div className={classes.wrapper}>
      <div className="ReactTable -striped -highlight">
        <div className={classes.controlsRow}>
          <div className={classes.navButtons}>
            <button
              type="button"
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className="-btn"
            >
              {t("reacttable-prev")}
            </button>

            <div className={classes.paginationSummary}>
              {t("reacttable-page-status", {
                current: tableState.pageIndex + 1,
                total: pageOptions.length || 1,
                defaultValue: `Página ${tableState.pageIndex + 1} de ${
                  pageOptions.length || 1
                }`,
              })}
            </div>

            <button
              type="button"
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className="-btn"
            >
              {t("reacttable-next")}
            </button>
          </div>

          <div className={classes.selectsWrap}>
            <FormControl
              fullWidth
              className={
                classes.selectControl + " " + classes.formControlMargins
              }
            >
              <Select
                MenuProps={{
                  className: classes.selectMenu,
                }}
                classes={{
                  select: classes.select,
                }}
                value={pageSelect}
                onChange={(event) => {
                  gotoPage(event.target.value);
                  setPageSelect(event.target.value);
                }}
                inputProps={{
                  name: "pageSelect",
                  id: "page-select",
                }}
              >
                {pageOptions.map((_, key) => (
                  <MenuItem
                    key={key}
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                    value={key}
                  >
                    {t("reacttable-page-option", { page: key + 1 })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              className={
                classes.selectControl + " " + classes.formControlMargins
              }
            >
              <Select
                MenuProps={{
                  className: classes.selectMenu,
                }}
                classes={{
                  select: classes.select,
                }}
                value={numberOfRows}
                onChange={(event) => {
                  setPageSize(event.target.value);
                  setNumberOfRows(event.target.value);
                }}
                inputProps={{
                  name: "numberOfRows",
                  id: "number-of-rows",
                }}
              >
                {numberOfRowsData.map((rowsValue) => (
                  <MenuItem
                    key={rowsValue}
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                    value={rowsValue}
                  >
                    {t("reacttable-rows-option", { rows: rowsValue })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>

        <div className={classes.tableScroll}>
          <table {...getTableProps()} className={`rt-table ${classes.table}`}>
            <thead className="rt-thead -header">
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()} className="rt-tr">
                  {headerGroup.headers.map((column, key) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className={classnames("rt-th rt-resizable-header", {
                        "-cursor-pointer":
                          headerGroup.headers.length - 1 !== key,
                        "-sort-asc": column.isSorted && !column.isSortedDesc,
                        "-sort-desc": column.isSorted && column.isSortedDesc,
                      })}
                    >
                      <div
                        className={`rt-resizable-header-content ${classes.headerCell}`}
                      >
                        {column.render("Header")}
                      </div>
                      {/* Búsqueda de datos deshabilitada por errores */}
                      {/* <div>
                        {headerGroup.headers.length - 1 === key
                          ? null
                          : column.canFilter
                          ? column.render("Filter")
                          : null}
                      </div> */}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()} className="rt-tbody">
              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    className={classnames(
                      "rt-tr",
                      { " -odd": i % 2 === 0 },
                      { " -even": i % 2 === 1 }
                    )}
                  >
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} className="rt-td">
                        <div className={classes.cellContent}>
                          {cell.render("Cell")}
                        </div>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="pagination-bottom" />
      </div>
    </div>
  );
}

export default Table;